import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@/shared/lib/utils';
import { Button } from '@/shared/ui/button/Button';
import type { FormFieldSize } from '../../helpers/field-size';
import type { SelectOption } from '../helpers/types';
import { normalizeOptionValue } from '../helpers/utils';
import { SearchableSingleSelect } from './SearchableSingleSelect';
import { SelectContent } from './SelectContent';
import { SelectItem } from './SelectItem';
import { SelectRoot } from './SelectRoot';
import { SelectTrigger } from './SelectTrigger';
import { SelectValue } from './SelectValue';

type SingleSelectProps = {
  /** フィルタリング済みオプション */
  filteredOptions: SelectOption[];
  /** 検索文字列 */
  searchValue: string;
  /** 検索文字列変更時のコールバック */
  onSearchValueChange: (value: string) => void;
  /** 選択された値 */
  selectValue: string;
  /** 値変更時のコールバック */
  onValueChange: (value: string) => void;
  /** 開閉状態変更時のコールバック */
  onOpenChange: (open: boolean) => void;
  /** クリア処理 */
  onClear: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** 検索機能を有効にする */
  showSearch: boolean;
  /** フィールドの最小幅 */
  size: FormFieldSize;
  /** 無効化フラグ */
  disabled: boolean;
  /** ルート要素のカスタムクラス名 */
  rootClassName?: string;
  /** トリガーボタンのカスタムクラス名 */
  triggerClassName?: string;
  /** ドロップダウンコンテンツのカスタムクラス名 */
  contentClassName?: string;
  /** フィールドのID */
  id?: string;
  /** クリアボタンを表示するか */
  showAllClear: boolean;
  /** プレースホルダー */
  placeholder: string;
  /** 固定された下部オプション */
  fixedBottomOptions?: SelectOption[];
  /** エラー状態 */
  hasError?: boolean;
  /** キーボードナビゲーション用のアクティブインデックス */
  activeIndex: number;
  /** 検索時のキーボードイベントハンドラー */
  onSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** 検索付き Popover の開閉状態 */
  searchOpen: boolean;
  /** 検索付き Popover の開閉変更ハンドラー */
  onSearchOpenChange: (open: boolean) => void;
  /** 全オプション（selectedLabel 解決用） */
  options: SelectOption[];
};

/**
 * 単一選択モードの Select コンポーネント
 */
export const SingleSelect = ({
  filteredOptions,
  searchValue,
  onSearchValueChange,
  selectValue,
  onValueChange,
  onOpenChange,
  onClear,
  showSearch,
  size,
  disabled,
  rootClassName,
  triggerClassName,
  contentClassName,
  id,
  showAllClear,
  placeholder,
  fixedBottomOptions,
  hasError = false,
  activeIndex,
  onSearchKeyDown,
  searchOpen,
  onSearchOpenChange,
  options,
}: SingleSelectProps) => {
  const hasSingleValue = selectValue !== '';
  const shouldShowClearButton = showAllClear && hasSingleValue && !disabled;

  // showSearch=true 時は Popover ベースの実装に切り替え
  if (showSearch) {
    return (
      <SearchableSingleSelect
        filteredOptions={filteredOptions}
        searchValue={searchValue}
        onSearchValueChange={onSearchValueChange}
        selectValue={selectValue}
        onValueChange={onValueChange}
        onClear={onClear}
        size={size}
        disabled={disabled}
        rootClassName={rootClassName}
        triggerClassName={triggerClassName}
        contentClassName={contentClassName}
        id={id}
        showAllClear={showAllClear}
        placeholder={placeholder}
        fixedBottomOptions={fixedBottomOptions}
        hasError={hasError}
        activeIndex={activeIndex}
        onSearchKeyDown={onSearchKeyDown}
        searchOpen={searchOpen}
        onSearchOpenChange={onSearchOpenChange}
        options={options}
      />
    );
  }

  // showSearch=false 時は従来の Radix Select 実装
  return (
    <div className={cn('relative w-full', rootClassName)}>
      <SelectRoot
        value={selectValue}
        onValueChange={onValueChange}
        onOpenChange={onOpenChange}
        disabled={disabled}
      >
        <SelectTrigger
          shouldShowClearButton={shouldShowClearButton}
          size={size}
          className={cn(shouldShowClearButton && 'pr-10', triggerClassName)}
          id={id}
          aria-invalid={hasError}
        >
          <SelectValue placeholder={<span className="text-text-muted">{placeholder}</span>} />
        </SelectTrigger>
        <SelectContent className={contentClassName} fixedBottomOptions={fixedBottomOptions}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <SelectItem
                key={normalizeOptionValue(option.value)}
                value={normalizeOptionValue(option.value)}
              >
                {option.label}
              </SelectItem>
            ))
          ) : (
            <div className="py-6 text-center text-sm text-text-muted">検索結果がありません</div>
          )}
        </SelectContent>
      </SelectRoot>
      {shouldShowClearButton && (
        <Button
          variant="ghost"
          size="none"
          onClick={onClear}
          className="absolute top-1/2 right-3 -translate-y-1/2"
          aria-label="選択をクリア"
          tabIndex={-1}
        >
          <XMarkIcon className="text-text-muted" />
        </Button>
      )}
    </div>
  );
};
